import {
    FetchState,
    BannerState,
    NewPartnerBanner,
    PartnerBanner,
    UpdatePartnerBannerOrderRequest,
    DisplayDefaultBannersRequest
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { defineStore } from "pinia";
import * as UserService from "@/shared/api/userService";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: BannerState = {
    fetchState: FetchState.Uninitialized,
    defaultBanners: [],
    customBanners: [],
    displayBanners: [],
    displayDefaultBanners: true
};

export const useBannerStore = defineStore("banner", {
    state: (): BannerState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
    },
    actions: {
        setDefaultBanners (banners: PartnerBanner[]) {
            this.defaultBanners = banners;
        },
        setCustomBanners (banners: PartnerBanner[]) {
            this.customBanners = banners;
        },
        setDispalyBanners (banners: PartnerBanner[]) {
            this.displayBanners = banners;
        },
        addCustomBanner (banner: PartnerBanner) {
            this.customBanners.push(banner);
        },
        updateCustomBanner (banner: PartnerBanner) {
            const bannerIndex = this.customBanners.findIndex(b => b.bannerId === banner.bannerId);
            this.customBanners.splice(bannerIndex, 1, banner);
        },
        removeCustomBanner (bannerId: number) {
            this.customBanners = this.customBanners.filter(b => b.bannerId !== bannerId)
                .map((b, i) => ({ ...b, carouselOrder: i + 1 }));
        },
        setDisplayDefaultBanners (value: boolean) {
            this.displayDefaultBanners = value;
        },
        setFetchState (state: FetchState) {
            this.fetchState = state;
        },
        getDefaultPartnerBanners () {
            this.setFetchState(FetchState.Loading);
            return UserService.getDefaultPartnerBanners()
                .then(resp => {
                    this.setDefaultBanners(resp.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    this.setFetchState(FetchState.Failed);
                });
        },
        getCustomPartnerBanners (partnerId: number) {
            this.setFetchState(FetchState.Loading);
            return UserService.getCustomPartnerBanners(partnerId)
                .then(resp => {
                    this.setCustomBanners(resp.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    this.setFetchState(FetchState.Failed);
                });
        },
        getPartnerBanners (employerId: number) {
            this.setFetchState(FetchState.Loading);
            return UserService.getPartnerBanners(employerId)
                .then(resp => {
                    this.setDispalyBanners(resp.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return [];
                });
        },
        createPartnerBanner (partnerId: number, banner: NewPartnerBanner): Promise<any> {
            return UserService.createPartnerBanner(partnerId, banner)
                .then(resp => {
                    if (resp.data) {
                        this.addCustomBanner(resp.data);
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("New banner created");
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return;
                });
        },
        updatePartnerBanner (partnerId: number,  bannerId: string, banner: PartnerBanner): Promise<any> {
            return UserService.updatePartnerBanner(partnerId, bannerId, banner)
                .then(resp => {
                    if (resp.data) {
                        this.updateCustomBanner(resp.data);
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("Banner changes saved");
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return;
                });
        },
        deletePartnerBanner (partnerId: number, bannerId: string): Promise<void> {
            return UserService.deletePartnerBanner(partnerId, bannerId).then(resp => {
                if (resp) {
                    this.removeCustomBanner(bannerId);
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Banner deleted successfully.");
                }
            })
            .catch (err => {
                storeHelper.handleError({ error: err, rethrow: true });
                return;
            });
        },
        updatePartnerBannerOrder (partnerId: number, bannerOrderRequest: UpdatePartnerBannerOrderRequest): Promise<any> {
            return UserService.updatePartnerBannerOrder(partnerId, bannerOrderRequest)
                .then(resp => {
                    if (resp.data) {
                        this.setCustomBanners(resp.data);
                        return resp.data;
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return;
                });
        },
        getDisplayDefaultBanners (partnerId: number): Promise<any> {
            return UserService.getDisplayDefaultBanners(partnerId)
                .then(resp => {
                    if (resp.data) {
                        this.setDisplayDefaultBanners(resp.data.isDisplayingDefaultBanners);
                        return resp.data;
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return;
                });
        },
        updateDisplayDefaultBanners (partnerId: number, displayDefaultBannersRequest: DisplayDefaultBannersRequest): Promise<any> {
            return UserService.setDisplayDefaultBanners(partnerId, displayDefaultBannersRequest)
                .then(resp => {
                    this.setDisplayDefaultBanners(displayDefaultBannersRequest.isDisplayingDefaultBanners);
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Banner display preference change saved.");
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    return;
                });
        }
    }
});